/**
 * AudioEngine — coração de áudio do VOCAL STUDIO.
 *
 * Responsabilidades:
 *  - AudioContext único (criado após gesto do usuário, por causa das
 *    políticas de autoplay);
 *  - carregamento dos AudioWorklets (captura, gate, detune);
 *  - bus master com limiter + medidor;
 *  - buses compartilhados de reverb (ConvolverNode com IR procedural) e delay;
 *  - cadeias de FX por faixa (TrackChain);
 *  - metrônomo e tons de referência com agendamento sample-accurate;
 *  - medição honesta de latência de saída.
 */
import type { FxConfig, MeterReading, ReverbPresetId } from '../types.js';
import { buildFxChain, configureDelayBus, configureFx, type CtxLike, type FxRefs } from './fxChain.js';
import { createImpulseResponse, REVERB_SPECS } from './fx.js';
import { clamp, dbToGain, gainToDb } from '../utils/misc.js';
import { toMonoBuffer } from './buffers.js';

export interface DelayBus {
  input: GainNode;
  delay: DelayNode;
  feedback: GainNode;
  tone: BiquadFilterNode;
  wet: GainNode;
}

export interface ReverbBus {
  input: GainNode;
  convolver: ConvolverNode;
  wet: GainNode;
}

export interface TrackChain {
  id: string;
  refs: FxRefs;
  input: GainNode;
  output: GainNode;
  volume: GainNode;
  pan: StereoPannerNode;
  muteGain: GainNode;
  analyser: AnalyserNode;
  sources: AudioBufferSourceNode[];
  /** Buses de efeitos independentes por faixa (presets não colidem entre si). */
  reverbBus: ReverbBus;
  delayBus: DelayBus;
  /** Buffer original e versão com redução de ruído (se calculada). */
  buffer: AudioBuffer | null;
  denoised: AudioBuffer | null;
  fx: FxConfig;
  peakHold: number;
  clipping: boolean;
}

export interface InstrumentalChain {
  volume: GainNode;
  pan: StereoPannerNode;
  muteGain: GainNode;
  analyser: AnalyserNode;
  sources: AudioBufferSourceNode[];
  buffer: AudioBuffer | null;
  peakHold: number;
  clipping: boolean;
}

export interface MasterChain {
  input: GainNode;
  limiter: DynamicsCompressorNode;
  limiterEnabledGain: GainNode;
  limiterBypassGain: GainNode;
  volume: GainNode;
  analyser: AnalyserNode;
  destination: MediaStreamAudioDestinationNode | null;
}

export interface LatencyInfo {
  inputLatencyMs: number;
  outputLatencyMs: number;
  method: string;
  roundTripMs?: number;
}

const WORKLET_URLS = {
  'vocal-gate': new URL('../workers/gate-processor.js', import.meta.url).href,
  'vocal-detune': new URL('../workers/detune-processor.js', import.meta.url).href,
  'vocal-deesser': new URL('../workers/deesser-processor.js', import.meta.url).href,
} as const;

export class AudioEngine {
  ctx: AudioContext | null = null;
  worklets: Record<string, boolean> = {};
  master!: MasterChain;
  monitorGain: GainNode | null = null;
  monitorSource: MediaStreamAudioSourceNode | null = null;
  instrumental: InstrumentalChain | null = null;
  private tracks = new Map<string, TrackChain>();
  private impulses = new Map<ReverbPresetId, AudioBuffer>();
  private workletPromise: Promise<void> | null = null;
  private meterTimers = new Map<string, number>();
  lastLatency: LatencyInfo | null = null;

  get sampleRate(): number {
    return this.ctx?.sampleRate ?? 48000;
  }

  get ready(): boolean {
    return !!this.ctx;
  }

  get currentTime(): number {
    return this.ctx?.currentTime ?? 0;
  }

  /**
   * Cria/retoma o AudioContext. Deve ser chamado a partir de um gesto do
   * usuário (clique/toque), senão o navegador mantém o contexto suspenso.
   */
  async ensure(): Promise<AudioContext> {
    if (!this.ctx) {
      const Ctor: typeof AudioContext =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctor) throw new Error('Web Audio API não suportada neste navegador.');
      this.ctx = new Ctor({ latencyHint: 'interactive' });
      this.buildMaster();
    }
    if (this.ctx.state === 'suspended') {
      try {
        await this.ctx.resume();
      } catch {
        /* alguns navegadores exigem novo gesto */
      }
    }
    await this.loadWorklets();
    return this.ctx;
  }

  async loadWorklets(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    if (!ctx.audioWorklet) {
      this.worklets = { 'vocal-capture': false, 'vocal-gate': false, 'vocal-detune': false, 'vocal-deesser': false };
      return;
    }
    if (this.workletPromise) return this.workletPromise;
    this.workletPromise = (async () => {
      for (const [name, url] of Object.entries(WORKLET_URLS)) {
        try {
          await ctx.audioWorklet.addModule(url);
          this.worklets[name] = true;
        } catch (err) {
          console.warn(`[AudioEngine] worklet ${name} indisponível:`, err);
          this.worklets[name] = false;
        }
      }
    })();
    return this.workletPromise;
  }

  private buildMaster() {
    const ctx = this.ctx!;
    const input = ctx.createGain();
    const limiterEnabledGain = ctx.createGain();
    const limiterBypassGain = ctx.createGain();
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -1.2;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.002;
    limiter.release.value = 0.12;
    const volume = ctx.createGain();
    volume.gain.value = 1;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0;

    input.connect(limiterEnabledGain);
    input.connect(limiterBypassGain);
    limiterEnabledGain.connect(limiter);
    limiter.connect(volume);
    limiterBypassGain.connect(volume);
    limiterBypassGain.gain.value = 0;
    volume.connect(analyser);
    analyser.connect(ctx.destination);

    this.master = {
      input,
      limiter,
      limiterEnabledGain,
      limiterBypassGain,
      volume,
      analyser,
      destination: null,
    };
  }

  private createTrackReverbBus(preset: ReverbPresetId): ReverbBus {
    const ctx = this.ctx!;
    const input = ctx.createGain();
    const convolver = ctx.createConvolver();
    const wet = ctx.createGain();
    convolver.buffer = this.getImpulse(preset);
    input.connect(convolver);
    convolver.connect(wet);
    wet.connect(this.master.input);
    return { input, convolver, wet };
  }

  private createTrackDelayBus(timeMs: number, feedbackAmount: number): DelayBus {
    const ctx = this.ctx!;
    const input = ctx.createGain();
    const delay = ctx.createDelay(2);
    const feedback = ctx.createGain();
    const tone = ctx.createBiquadFilter();
    tone.type = 'lowpass';
    tone.frequency.value = 4200;
    const wet = ctx.createGain();
    configureDelayBus({ delay, feedback, wet }, timeMs, feedbackAmount);
    input.connect(delay);
    delay.connect(tone);
    tone.connect(feedback);
    feedback.connect(delay);
    delay.connect(wet);
    wet.connect(this.master.input);
    return { input, delay, feedback, tone, wet };
  }

  getImpulse(preset: ReverbPresetId): AudioBuffer {
    const ctx = this.ctx!;
    let ir = this.impulses.get(preset);
    if (!ir) {
      ir = createImpulseResponse(ctx, REVERB_SPECS[preset]);
      this.impulses.set(preset, ir);
    }
    return ir;
  }

  /** Cria uma faixa (cadeia de FX própria) ligada ao master. */
  createTrackChain(id: string, fx: FxConfig): TrackChain {
    const ctx = this.ctx!;
    const reverbBus = this.createTrackReverbBus(fx.reverb.preset);
    const delayBus = this.createTrackDelayBus(fx.delay.timeMs, fx.delay.feedback);
    const { refs, input, output } = buildFxChain({
      ctx: ctx as unknown as CtxLike,
      sends: { reverbIn: reverbBus.input, delayIn: delayBus.input },
      worklets: this.worklets,
    });
    const volume = ctx.createGain();
    const pan = ctx.createStereoPanner();
    const muteGain = ctx.createGain();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0;

    // input já percorre a cadeia completa montada por buildFxChain; não conecte
    // input diretamente a output (isso bypassaria todos os efeitos).
    output.connect(volume);
    volume.connect(pan);
    pan.connect(muteGain);
    muteGain.connect(analyser);
    analyser.connect(this.master.input);

    const chain: TrackChain = {
      id,
      refs,
      input: input as GainNode,
      output: output as GainNode,
      volume,
      pan,
      muteGain,
      analyser,
      sources: [],
      reverbBus,
      delayBus,
      buffer: null,
      denoised: null,
      fx,
      peakHold: 0,
      clipping: false,
    };
    configureFx(refs, fx, ctx.currentTime);
    this.tracks.set(id, chain);
    return chain;
  }

  // ------------------------------------------------------------------
  // Bus do instrumental (arquivo do usuário ou base de referência local)
  // ------------------------------------------------------------------

  buildInstrumentalChain(): InstrumentalChain {
    const ctx = this.ctx!;
    if (this.instrumental) return this.instrumental;
    const volume = ctx.createGain();
    const pan = ctx.createStereoPanner();
    const muteGain = ctx.createGain();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0;
    volume.connect(pan);
    pan.connect(muteGain);
    muteGain.connect(analyser);
    analyser.connect(this.master.input);
    this.instrumental = { volume, pan, muteGain, analyser, sources: [], buffer: null, peakHold: 0, clipping: false };
    return this.instrumental;
  }

  setInstrumentalBuffer(buffer: AudioBuffer | null) {
    // Project/document state can be restored before the first user gesture
    // creates an AudioContext. Cache the buffer intent without constructing nodes.
    if (!this.ctx) return null;
    const chain = this.buildInstrumentalChain();
    chain.buffer = buffer;
    return chain;
  }

  /**
   * Toca o instrumental com fade in/out aplicados por automação de ganho
   * (o buffer original nunca é alterado).
   */
  playInstrumental(opts: {
    when: number;
    offsetSec: number;
    fadeInMs?: number;
    fadeOutMs?: number;
    playbackRate?: number;
    durationSec?: number;
  }): AudioBufferSourceNode | null {
    const ctx = this.ctx;
    const chain = this.instrumental;
    if (!ctx || !chain?.buffer) return null;
    const buf = chain.buffer;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    if (opts.playbackRate) src.playbackRate.value = clamp(opts.playbackRate, 0.5, 2);
    const gain = ctx.createGain();
    gain.gain.value = 1;
    src.connect(gain);
    gain.connect(chain.volume);

    const start = Math.max(ctx.currentTime, opts.when);
    const fi = Math.max(0, (opts.fadeInMs ?? 0) / 1000);
    const offset = clamp(opts.offsetSec, 0, Math.max(0, buf.duration - 0.005));
    const total = opts.durationSec ?? buf.duration - offset;
    if (fi > 0) {
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.linearRampToValueAtTime(1, start + fi);
    }
    const fo = Math.max(0, (opts.fadeOutMs ?? 0) / 1000);
    if (fo > 0 && total > fo) {
      gain.gain.setValueAtTime(1, start + Math.max(0, total - fo));
      gain.gain.linearRampToValueAtTime(0.0001, start + total);
    }
    try {
      src.start(start, offset);
      if (isFinite(total) && total > 0) src.stop(start + total + 0.05);
    } catch (err) {
      console.warn('[AudioEngine] falha ao iniciar instrumental:', err);
      return null;
    }
    chain.sources.push(src);
    src.onended = () => {
      chain.sources = chain.sources.filter((s) => s !== src);
      try {
        src.disconnect();
        gain.disconnect();
      } catch {
        /* noop */
      }
    };
    return src;
  }

  stopInstrumental(when?: number) {
    const chain = this.instrumental;
    if (!chain) return;
    const at = when ?? this.ctx?.currentTime ?? 0;
    for (const s of [...chain.sources]) {
      try {
        s.stop(at);
      } catch {
        /* já parado */
      }
    }
    chain.sources = [];
  }

  setInstrumentalVolume(v: number) {
    const ctx = this.ctx;
    const chain = this.instrumental;
    if (ctx && chain) chain.volume.gain.setTargetAtTime(clamp(v, 0, 2), ctx.currentTime, 0.015);
  }

  setInstrumentalPan(p: number) {
    const ctx = this.ctx;
    const chain = this.instrumental;
    if (ctx && chain) chain.pan.pan.setTargetAtTime(clamp(p, -1, 1), ctx.currentTime, 0.015);
  }

  setInstrumentalMuted(muted: boolean) {
    const ctx = this.ctx;
    const chain = this.instrumental;
    if (ctx && chain) chain.muteGain.gain.setTargetAtTime(muted ? 0 : 1, ctx.currentTime, 0.01);
  }

  readInstrumental(): MeterReading | null {
    if (!this.instrumental) return null;
    return this.readAnalyser(this.instrumental.analyser, this.instrumental);
  }

  getTrack(id: string): TrackChain | undefined {
    return this.tracks.get(id);
  }

  removeTrackChain(id: string) {
    const t = this.tracks.get(id);
    if (!t) return;
    this.stopTrackSources(id);
    try {
      t.analyser.disconnect();
      t.muteGain.disconnect();
      t.reverbBus.input.disconnect(); t.reverbBus.convolver.disconnect(); t.reverbBus.wet.disconnect();
      t.delayBus.input.disconnect(); t.delayBus.delay.disconnect(); t.delayBus.feedback.disconnect(); t.delayBus.tone.disconnect(); t.delayBus.wet.disconnect();
    } catch {
      /* noop */
    }
    this.tracks.delete(id);
  }

  applyFx(id: string, fx: FxConfig) {
    const t = this.tracks.get(id);
    if (!t || !this.ctx) return;
    t.fx = fx;
    configureFx(t.refs, fx, this.ctx.currentTime);
    t.reverbBus.convolver.buffer = this.getImpulse(fx.reverb.preset);
    configureDelayBus(t.delayBus, fx.delay.timeMs, fx.delay.feedback);
  }

  setTrackVolume(id: string, v: number) {
    const t = this.tracks.get(id);
    if (t && this.ctx) t.volume.gain.setTargetAtTime(clamp(v, 0, 2), this.ctx.currentTime, 0.01);
  }

  setTrackPan(id: string, p: number) {
    const t = this.tracks.get(id);
    if (t && this.ctx) t.pan.pan.setTargetAtTime(clamp(p, -1, 1), this.ctx.currentTime, 0.01);
  }

  setTrackMuted(id: string, muted: boolean) {
    const t = this.tracks.get(id);
    if (t && this.ctx) t.muteGain.gain.setTargetAtTime(muted ? 0 : 1, this.ctx.currentTime, 0.008);
  }

  setMasterVolume(v: number) {
    if (this.ctx) this.master.volume.gain.setTargetAtTime(clamp(v, 0, 1.5), this.ctx.currentTime, 0.01);
  }

  setMasterLimiter(enabled: boolean, ceilingDb = -1.2) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.master.limiter.threshold.setTargetAtTime(clamp(ceilingDb, -30, 0), t, 0.02);
    this.master.limiterEnabledGain.gain.setTargetAtTime(enabled ? 1 : 0, t, 0.01);
    this.master.limiterBypassGain.gain.setTargetAtTime(enabled ? 0 : 1, t, 0.01);
  }

  // ------------------------------------------------------------------
  // Fontes (reprodução de faixas)
  // ------------------------------------------------------------------

  setTrackBuffer(id: string, data: Float32Array, sampleRate?: number) {
    const ctx = this.ctx;
    const t = this.tracks.get(id);
    if (!ctx || !t) return null;
    const buf = toMonoBuffer(ctx, data, sampleRate);
    t.buffer = buf;
    return buf;
  }

  setTrackDenoised(id: string, data: Float32Array | null, sampleRate?: number) {
    const ctx = this.ctx;
    const t = this.tracks.get(id);
    if (!ctx || !t) return;
    t.denoised = data ? toMonoBuffer(ctx, data, sampleRate) : null;
    // Se a redução de ruído está ligada, troca a fonte ativa sem cortar o resto.
    for (const s of t.sources) {
      if (!data) continue;
      // Reinicia apenas se estiver tocando; caso contrário vale no próximo play.
    }
  }

  /** Toca a faixa a partir de `offsetSec`, agendada em `when` (ctx time). */
  playTrack(
    id: string,
    opts: { when: number; offsetSec: number; useDenoised?: boolean; playbackRate?: number; loop?: boolean }
  ): AudioBufferSourceNode | null {
    const ctx = this.ctx;
    const t = this.tracks.get(id);
    if (!ctx || !t) return null;
    const buf = (opts.useDenoised && t.denoised) || t.buffer;
    if (!buf || buf.length === 0) return null;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = !!opts.loop;
    if (opts.playbackRate) src.playbackRate.value = clamp(opts.playbackRate, 0.5, 2);
    src.connect(t.input);
    const offset = clamp(opts.offsetSec, 0, Math.max(0, buf.duration - 0.005));
    try {
      src.start(Math.max(ctx.currentTime, opts.when), offset);
    } catch (err) {
      console.warn('[AudioEngine] falha ao iniciar fonte:', err);
      return null;
    }
    t.sources.push(src);
    src.onended = () => {
      t.sources = t.sources.filter((s) => s !== src);
      try {
        src.disconnect();
      } catch {
        /* noop */
      }
    };
    return src;
  }

  stopTrackSources(id: string, when?: number) {
    const t = this.tracks.get(id);
    if (!t) return;
    const at = when ?? this.ctx?.currentTime ?? 0;
    for (const s of [...t.sources]) {
      try {
        s.stop(at);
      } catch {
        /* já parado */
      }
    }
    t.sources = [];
  }

  // ------------------------------------------------------------------
  // Monitoração do microfone (🎧 ouvir minha voz)
  // ------------------------------------------------------------------

  connectMonitor(stream: MediaStream, enabled: boolean, volume = 1) {
    const ctx = this.ctx;
    if (!ctx) return;
    if (!this.monitorGain) {
      this.monitorGain = ctx.createGain();
      this.monitorGain.gain.value = 0;
      this.monitorGain.connect(this.master.input);
    }
    try { this.monitorSource?.disconnect(); } catch { /* noop */ }
    this.monitorSource = ctx.createMediaStreamSource(stream);
    this.monitorSource.connect(this.monitorGain);
    this.monitorGain.gain.setTargetAtTime(enabled ? clamp(volume, 0, 1.5) : 0, ctx.currentTime, 0.02);
  }

  setMonitor(enabled: boolean, volume = 1) {
    if (this.monitorGain && this.ctx) {
      this.monitorGain.gain.setTargetAtTime(enabled ? clamp(volume, 0, 1.5) : 0, this.ctx.currentTime, 0.02);
    }
  }

  // ------------------------------------------------------------------
  // Metrônomo / tons de referência (agendamento sample-accurate)
  // ------------------------------------------------------------------

  /** Agenda um clique em `at` (ctx time). */
  scheduleClick(at: number, opts: { freq?: number; gain?: number; duration?: number } = {}) {
    const ctx = this.ctx;
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = opts.freq ?? 1500;
    const dur = opts.duration ?? 0.045;
    const peak = opts.gain ?? 0.5;
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), at + 0.002);
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    osc.connect(g);
    g.connect(this.master.input);
    osc.start(at);
    osc.stop(at + dur + 0.02);
    osc.onended = () => {
      try {
        osc.disconnect();
        g.disconnect();
      } catch {
        /* noop */
      }
    };
  }

  /**
   * Agenda `beats` cliques no BPM dado, começando em `startAt`.
   * Retorna o ctx time do último clique (início "real" da gravação).
   */
  scheduleCountdown(
    startAt: number,
    opts: { beats?: number; seconds?: number; bpm?: number; gain?: number }
  ): number {
    const ctx = this.ctx;
    if (!ctx) return startAt;
    const beats = opts.beats ?? 0;
    const seconds = opts.seconds ?? 0;
    const bpm = opts.bpm ?? 90;
    const step = 60 / bpm;
    let t = startAt;
    for (let i = 0; i < beats; i++) {
      this.scheduleClick(t, { freq: i === 0 ? 2000 : 1500, gain: opts.gain ?? 0.5 });
      t += step;
    }
    for (let i = 0; i < seconds; i++) {
      this.scheduleClick(t, { freq: i === seconds - 1 ? 2000 : 1200, gain: opts.gain ?? 0.5 });
      t += 1;
    }
    return t;
  }

  scheduleMetronome(from: number, bpm: number, beats: number, gain: number, offsetBeats = 0) {
    const step = 60 / Math.max(20, bpm);
    for (let i = 0; i < beats; i++) {
      const b = i + offsetBeats;
      this.scheduleClick(from + i * step, {
        freq: b % 4 === 0 ? 2000 : 1400,
        gain: gain * (b % 4 === 0 ? 1 : 0.75),
      });
    }
  }

  // ------------------------------------------------------------------
  // Latência
  // ------------------------------------------------------------------

  /**
   * Mede/estima a latência. Sempre diz qual método usou — nunca finge
   * precisão que não tem.
   */
  measureLatency(): LatencyInfo {
    const ctx = this.ctx;
    if (!ctx) {
      return { inputLatencyMs: 0, outputLatencyMs: 0, method: 'contexto não iniciado' };
    }
    const base = (ctx as any).baseLatency ?? 0;
    const out = (ctx as any).outputLatency;
    let outputLatencyMs: number;
    let method: string;
    if (typeof out === 'number' && out > 0) {
      outputLatencyMs = out * 1000;
      method = 'ctx.outputLatency (medido pelo navegador)';
    } else {
      // Estimativa conservadora: buffer de saída típico + base.
      outputLatencyMs = base * 1000 + 20;
      method = 'estimativa (ctx.outputLatency indisponível neste navegador)';
    }
    const info: LatencyInfo = {
      // baseLatency descreve atraso de saída do grafo; entrada do microfone
      // só é preenchida quando MediaTrackSettings.latency está disponível.
      inputLatencyMs: 0,
      outputLatencyMs,
      method,
    };
    this.lastLatency = info;
    return info;
  }

  // ------------------------------------------------------------------
  // Medidores
  // ------------------------------------------------------------------

  readAnalyser(analyser: AnalyserNode, hold: { peakHold: number; clipping: boolean }): MeterReading {
    const data = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(data);
    let sum = 0;
    let p = 0;
    let clip = 0;
    for (let i = 0; i < data.length; i++) {
      const v = data[i];
      sum += v * v;
      const a = Math.abs(v);
      if (a > p) p = a;
      if (a >= 0.999) clip++;
    }
    const r = Math.sqrt(sum / Math.max(1, data.length));
    hold.peakHold = Math.max(p, hold.peakHold * 0.94);
    if (clip > 2) hold.clipping = true;
    return {
      rms: r,
      peak: p,
      dbfs: gainToDb(r),
      peakDb: gainToDb(p),
      clipping: clip > 2 || p >= 0.999,
    };
  }

  readMaster(): MeterReading | null {
    if (!this.ctx) return null;
    const hold = { peakHold: 0, clipping: false };
    return this.readAnalyser(this.master.analyser, hold);
  }

  readTrack(id: string): MeterReading | null {
    const t = this.tracks.get(id);
    if (!t) return null;
    return this.readAnalyser(t.analyser, t);
  }

  resetClipFlags() {
    for (const t of this.tracks.values()) t.clipping = false;
  }

  // ------------------------------------------------------------------

  suspend() {
    this.ctx?.suspend().catch(() => {});
  }

  async resume() {
    if (this.ctx && this.ctx.state !== 'running') await this.ctx.resume().catch(() => {});
  }

  /** Usado em testes: descarta o contexto. */
  dispose() {
    this.tracks.clear();
    this.impulses.clear();
    try { this.monitorSource?.disconnect(); this.monitorGain?.disconnect(); } catch { /* noop */ }
    this.monitorSource = null;
    this.monitorGain = null;
    this.instrumental = null;
    this.ctx?.close().catch(() => {});
    this.ctx = null;
    this.workletPromise = null;
  }
}

/** Instância única (client-side first: um só AudioContext por aba). */
export const audioEngine = new AudioEngine();

/** Helpers para construir a mesma cadeia em contexto offline (exportação). */
export function buildOfflineFx(
  ctx: OfflineAudioContext,
  fx: FxConfig,
  sends: { reverbIn: AudioNode; delayIn: AudioNode } | null
) {
  const built = buildFxChain({ ctx: ctx as unknown as CtxLike, sends, worklets: {} });
  configureFx(built.refs, fx, 0);
  return built;
}

export { dbToGain };
